<script>
  import { onMount, onDestroy } from 'svelte';
  import { getApiUrl } from '../api';
  import { getRelativeTime, formatDate, getCurrencySymbol } from '../utils';
  import { authStore } from '../authStore';
  import { joinedChallenges } from '../achievementStore';
  import { Link, navigate } from 'svelte-routing';
  import LogTransactionModal from '../components/LogTransactionModal.svelte';
  import { t } from '../i18nStore';
  import { authFetch } from '../authService';

  export let id; 

  let promo = null;
  let isLoading = true;
  let error = '';
  let currentUser = null;
  let achievementId = null;
  let showLogModal = false;
  let currency = '$';

  let isAdmin = false;
  const unsubscribeAuth = authStore.subscribe(value => {
    currentUser = value.user;
    isAdmin = value.isAdmin;
  });

  const unsubscribeChallenges = joinedChallenges.subscribe(map => {
    if (promo && map[promo._id]) {
      achievementId = map[promo._id];
    } else {
      achievementId = null;
    }
  });

  onMount(async () => {
    try {
      const response = await fetch(getApiUrl(`/promotions/${id}`));
      if (!response.ok) throw new Error('Promotion not found.');
      promo = await response.json();
      currency = getCurrencySymbol(promo.countryCode);
      joinedChallenges.update(map => map);
    } catch (e) {
      error = e.message;
    } finally {
      isLoading = false;
    }
  });

  onDestroy(() => {
    unsubscribeAuth();
    unsubscribeChallenges();
  });

  function copyCode() {
    if (promo?.promoCode) {
      navigator.clipboard.writeText(promo.promoCode);
      alert("Code copied: " + promo.promoCode);
    }
  }

  async function handleJoinChallenge() {
    if (!currentUser) {
        alert($t('public_profile.login_required'));
        return;
    }
    try {
      const response = await fetch(getApiUrl('/achievements'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: currentUser.idTokenClaims.oid, promotionId: promo._id }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || "Failed to join challenge");
      
      joinedChallenges.update(map => {
          map[promo._id] = result.id || result.achievementId || true;
          return map;
      });
      alert("Challenge joined!");
    } catch (err) {
      alert(err.message);
    }
  }

  async function handleDeletePromotion() {
    if (!confirm($t('promo.delete_confirm'))) return;
    try {
      const res = await authFetch(getApiUrl(`/promotions/${promo._id}`), {
        method: 'DELETE'
      });
      const data = await res.json();
      if (!res.ok) {
        if (res.status === 400 && data.error && (data.error.includes("achievement") || data.error.includes("challenge"))) {
          alert($t('promo.error.cannot_delete_has_challenges'));
        } else {
          alert(data.error || 'Failed to delete promotion');
        }
        return;
      }
      alert($t('promo.delete_success'));
      navigate('/');
    } catch (err) {
      alert(err.message);
    }
  }

  async function handleBanPromotion() {
    const reason = prompt($t('promo.ban_reason_prompt'), 'Spam / low quality violation');
    if (reason === null) return;
    if (!confirm($t('promo.ban_confirm'))) return;
    try {
      const res = await authFetch(getApiUrl(`/admin/promotions/${promo._id}/ban`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to ban promotion');
      promo.isBanned = true;
      promo.verificationStatus = 'Rejected';
      promo = promo;
      alert($t('promo.banned_success'));
    } catch (err) {
      alert(err.message);
    }
  }

  let isRollingOver = false;
  async function handleTriggerRollOver() {
    if (!confirm(`Generate next cycle for "${promo.name}" immediately?`)) return;
    isRollingOver = true;
    try {
      const res = await authFetch(getApiUrl(`/admin/promotions/${promo._id}/recurrence/trigger`), {
        method: 'POST'
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to roll over promotion');
      alert(`Success! Generated next cycle: ${data.newPromotion?.name}`);
      if (data.newPromotion?._id) {
        navigate(`/promotions/detail/${data.newPromotion._id}`);
      }
    } catch (err) {
      alert(err.message);
    } finally {
      isRollingOver = false;
    }
  }

  $: hasChallenge = promo && ((promo.targetNumTransactions > 0) || (promo.minimumTargetAccumulatedSpending > 0) || (promo.maximumTargetNumTransactions > 0) || (promo.maximumTargetAccumulatedSpending > 0));
  $: isAuthorOrAdmin = currentUser && promo && (promo.createdByUserId === currentUser.idTokenClaims?.oid || isAdmin);
  $: isCreator = currentUser && promo && (promo.createdByUserId === currentUser.idTokenClaims?.oid);
</script>

{#if currentUser && showLogModal && promo}
  <LogTransactionModal 
    bind:showModal={showLogModal} 
    achievementId={achievementId} 
    promotionName={promo.name} 
    on:success={() => alert("Transaction Logged!")}
    on:close={() => showLogModal = false}
  />
{/if}

<div class="page-wrapper">
  <div class="detail-container">
    {#if isLoading}
      <p class="status-msg">{$t('common.loading')}</p>
    {:else if error}
      <p class="error">{error}</p>
    {:else if promo}
      <div class="promo-header">
        {#if promo.isBanned}
          <div class="banned-banner">
            ⛔ <strong>{$t('promo.ban_btn')}:</strong> This promotion has been banned by an administrator and hidden from public searches.
          </div>
        {/if}
        <div class="title-row">
          <h1>{promo.name}</h1>
          <div class="badges">
            {#if promo.isBusiness}
              <span class="badge business">★ {$t('card.business_post')}</span>
            {/if}
            {#if promo.selectedMember}
              <span class="badge exclusive">{$t('card.selected_member')}</span>
            {/if}
            {#if promo.requireRegistration}
              <span class="badge registration">{$t('card.reg_req')}</span>
            {/if}
          </div>
        </div>
        
        {#if promo.promoCode}
          <!-- svelte-ignore a11y-click-events-have-key-events -->
          <!-- svelte-ignore a11y-interactive-supports-focus -->
          <div class="promo-code-box" on:click={copyCode} role="button">
              <span class="code-val">{promo.promoCode}</span>
              <span class="code-hint">📋 Click to Copy</span>
          </div>
        {/if}
      </div>

      <div class="main-layout">
        <div class="content-body">
          <!-- Overview Section -->
          <section class="info-card">
            <p class="description">{promo.description}</p>
            {#if promo.hyperlink}
              <a href={promo.hyperlink} target="_blank" rel="noopener noreferrer" class="btn-link">
                {$t('promo_detail.official_link')}
              </a>
            {/if}
          </section>

          <!-- Validity Section -->
          <section class="info-card">
            <h3>{$t('promo_detail.validity')}</h3>
            <div class="grid-2">
              <div class="item"><strong>{$t('card.starts')}:</strong> {formatDate(promo.startDate)}</div>
              <div class="item"><strong>{$t('card.ends')}:</strong> {formatDate(promo.endDate)}</div>
              {#if promo.eligibleDaysOfWeek?.length > 0}
                <div class="item full">
                  <strong>{$t('promo_detail.eligible_days')}:</strong> 
                  {promo.eligibleDaysOfWeek.map(d => $t(`promo_detail.days.${d}`)).join(', ')}
                </div>
              {/if}
              {#if promo.eligibleDates?.length > 0}
                <div class="item full">
                  <strong>{$t('promo_detail.specific_dates')}:</strong> 
                  {promo.eligibleDates.join(', ')}
                </div>
              {/if}
            </div>
          </section>

          <!-- Reward Schemes -->
          {#if (promo.rewardScheme?.length > 0) || (promo.rewardTiers?.length > 0)}
            <section class="info-card">
              <h3>{$t('promo_detail.rewards')}</h3>
              
              {#if promo.rewardScheme?.length > 0}
                <div class="scheme-list">
                  {#each promo.rewardScheme as rule}
                    <div class="scheme-item">
                      <span class="freq">Every {rule.frequency} txns</span>
                      <span class="val">
                        {#if rule.rewardType === 'Percentage'}
                          {rule.percentage}% 
                          {#if rule.maxRewardAmount > 0}(Capped at {currency}{rule.maxRewardAmount}){/if}
                        {:else}
                          {currency}{rule.fixedAmount} Fixed
                        {/if}
                      </span>
                    </div>
                  {/each}
                </div>
              {/if}

              {#if promo.rewardTiers?.length > 0}
                <div class="tier-table-container">
                  <h4>{$t('promo_detail.reward_tiers')}</h4>
                  <table class="tier-table">
                    <thead>
                      <tr>
                        <th>{$t('promo_detail.condition')}</th>
                        <th>{$t('promo_detail.reward_val')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {#each promo.rewardTiers as tier}
                        <tr>
                          <td>{tier.condition}</td>
                          <td>
                            {#if tier.rewardType === 'Percentage'}
                              {tier.percentage}% {tier.unitOfMeasure || ''}
                            {:else}
                              {currency}{tier.fixedAmount} {tier.unitOfMeasure || ''}
                            {/if}
                          </td>
                        </tr>
                      {/each}
                    </tbody>
                  </table>
                </div>
              {/if}

              <div class="reward-caps">
                {#if promo.maximumRewardPercentage}
                  <div class="cap-item"><strong>Max Reward %:</strong> {promo.maximumRewardPercentage}%</div>
                {/if}
                {#if promo.maximumReward}
                  <div class="cap-item"><strong>Max Reward $:</strong> {currency}{promo.maximumReward}</div>
                {/if}
              </div>
            </section>
          {/if}
        </div>

        <aside class="sidebar">
          <!-- Action Card -->
          <div class="sidebar-card action-card">
            {#if currentUser}
              {#if achievementId}
                <button class="btn-action log" on:click={() => showLogModal = true}>⚡ {$t('card.log')}</button>
              {:else if hasChallenge}
                <button class="btn-action join" on:click={handleJoinChallenge}>{$t('card.join')}</button>
              {/if}
              {#if isAuthorOrAdmin}
                <Link to={`/edit/${promo._id}`} class="btn-action edit-btn">{$t('card.edit')}</Link>
              {/if}
              {#if isCreator}
                <button class="btn-action delete-btn" on:click={handleDeletePromotion}>🗑️ {$t('promo.delete_btn')}</button>
              {/if}
              {#if isAdmin && !promo.isBanned}
                <button class="btn-action ban-btn" on:click={handleBanPromotion}>⛔ {$t('promo.ban_btn')}</button>
              {/if}
              {#if isAdmin && promo.recurrenceRule && promo.recurrenceRule.enabled}
                <button class="btn-action rollover-btn" on:click={handleTriggerRollOver} disabled={isRollingOver}>
                  {isRollingOver ? 'Rolling Over...' : '🔁 Roll Over to Next Period'}
                </button>
              {/if}
            {/if}
            
            <div class="stat-row">
              <strong>{$t('card.favourite')}:</strong> {promo.favouriteCount || 0}
            </div>
            {#if promo.requireRegistration && promo.registrationDetail}
               <div class="reg-detail">
                 <strong>{$t('promo_detail.reg_detail')}:</strong>
                 <p>{promo.registrationDetail}</p>
               </div>
            {/if}
          </div>

          <!-- Recurrence Info Card -->
          {#if promo.recurrenceRule && promo.recurrenceRule.enabled}
            <div class="sidebar-card recurrence-card" style="border-left: 4px solid #0284c7; background: #f0f9ff;">
              <h3 style="color: #0369a1;">🔁 Recurring Promotion</h3>
              <div class="meta-list">
                <div class="meta-item">
                  <span class="label">Frequency:</span>
                  <strong style="text-transform: capitalize; color: #0284c7;">{promo.recurrenceRule.frequency}</strong>
                </div>
                <div class="meta-item">
                  <span class="label">Campaign Cutoff:</span>
                  <strong>{promo.recurrenceRule.endDate ? promo.recurrenceRule.endDate.split('T')[0] : 'Continuous'}</strong>
                </div>
                <div class="meta-item">
                  <span class="label">Lead Days:</span>
                  <span>{promo.recurrenceRule.leadDays || 2} days before expiry</span>
                </div>
                <div class="meta-item">
                  <span class="label">Publish Mode:</span>
                  <span>{promo.recurrenceRule.autoPublish ? 'Auto-Publish' : 'Pending Review'}</span>
                </div>
              </div>
            </div>
          {/if}

          <!-- Spending Card -->
          <div class="sidebar-card">
            <h3>{$t('promo_detail.eligibility')}</h3>
            <div class="meta-list">
              <div class="meta-item">
                <span class="label">{$t('card.min_spend')}:</span>
                <span class="val">{currency}{(promo.minimumSpending || 0).toFixed(2)}</span>
              </div>
              {#if promo.targetNumTransactions != null && promo.targetNumTransactions > 0}
                <div class="meta-item">
                  <span class="label">Target Transactions:</span>
                  <span class="val">{promo.targetNumTransactions}</span>
                </div>
              {/if}
              {#if promo.maximumTargetNumTransactions != null && promo.maximumTargetNumTransactions > 0}
                <div class="meta-item">
                  <span class="label">Max Target Transactions:</span>
                  <span class="val">{promo.maximumTargetNumTransactions}</span>
                </div>
              {/if}
              {#if promo.minimumTargetAccumulatedSpending != null && promo.minimumTargetAccumulatedSpending > 0}
                <div class="meta-item">
                  <span class="label">Min Target Spend:</span>
                  <span class="val">{currency}{promo.minimumTargetAccumulatedSpending.toFixed(2)}</span>
                </div>
              {/if}
              {#if promo.maximumTargetAccumulatedSpending != null && promo.maximumTargetAccumulatedSpending > 0}
                <div class="meta-item">
                  <span class="label">Max Target Spend:</span>
                  <span class="val">{currency}{promo.maximumTargetAccumulatedSpending.toFixed(2)}</span>
                </div>
              {/if}
              {#if promo.methodsOfSpending?.length > 0}
                <div class="meta-item vertical">
                  <span class="label">{$t('admin.payment_methods')}:</span>
                  <div class="tags-mini">
                    {#each promo.methodsOfSpending as m}<span>{m}</span>{/each}
                  </div>
                </div>
              {/if}
            </div>
          </div>

          <!-- Banking Card -->
          {#if (promo.banks?.length > 0) || (promo.creditCards?.length > 0)}
            <div class="sidebar-card">
              <h3>{$t('promo_detail.banking')}</h3>
              {#if promo.banks?.length > 0}
                <div class="bank-list-side">{promo.banks.join(', ')}</div>
              {/if}
              {#if promo.creditCards?.length > 0}
                <div class="cc-list">
                  {#each promo.creditCards as card}
                    <div class="cc-item">
                      <span>💳 {card}</span>
                      <Link to={`/cardholders/${encodeURIComponent(card)}?promoId=${promo._id}`} class="btn-who">{$t('promo_detail.who_has_this_card')}</Link>
                    </div>
                  {/each}
                </div>
              {/if}
            </div>
          {/if}

          <!-- Availability Card -->
          <div class="sidebar-card">
            <h3>{$t('promo_detail.locations')}</h3>
            <div class="loc-list">
              <div class="loc-row">
                <span>{$t('card.online')}:</span>
                <strong>{promo.isOnline ? '✅' : '❌'}</strong>
              </div>
              {#if promo.isOnline && promo.onlineShops?.length > 0}
                <p class="loc-details">{promo.onlineShops.join(', ')}</p>
              {/if}
              
              <div class="loc-row">
                <span>{$t('card.in_store')}:</span>
                <strong>{promo.isInStore ? '✅' : '❌'}</strong>
              </div>
              {#if promo.isInStore}
                {#if promo.physicalStores?.length > 0}
                  <div class="loc-sub"><strong>{$t('promo_detail.physical_stores')}:</strong> {promo.physicalStores.join(', ')}</div>
                {/if}
                {#if promo.places?.length > 0}
                  <div class="loc-sub"><strong>{$t('promo_detail.places')}:</strong> {promo.places.join(', ')}</div>
                {/if}
              {/if}
            </div>
          </div>
        </aside>
      </div>

      <footer class="detail-footer">
        <div class="meta">
          <span>{$t('card.last_modified')} <strong>{promo.createdByUserName || 'Unknown'}</strong></span>
          <span class="dot">•</span>
          <span class="time">{getRelativeTime(promo.lastUpdated)}</span>
        </div>
      </footer>
    {/if}
  </div>
</div>

<style>
  .page-wrapper { background: #f4f7f6; min-height: 100vh; padding: 2rem 1rem; }
  .detail-container {
    max-width: 1200px; margin: 0 auto; background: #fff;
    border-radius: 12px; box-shadow: 0 10px 25px rgba(0,0,0,0.05);
    overflow: hidden; padding: 2rem;
  }
  
  .promo-header { border-bottom: 2px solid #f0f0f0; padding-bottom: 1.5rem; margin-bottom: 2rem; }
  .title-row { display: flex; justify-content: space-between; align-items: flex-start; flex-wrap: wrap; gap: 1rem; }
  h1 { margin: 0; font-size: 2rem; color: #1a1a1a; flex: 1; }
  .badges { display: flex; gap: 0.5rem; flex-wrap: wrap; }
  .badge { padding: 4px 12px; border-radius: 20px; font-size: 0.85rem; font-weight: bold; }
  .business { background: #fff3cd; color: #856404; }
  .exclusive { background: #f8d7da; color: #721c24; }
  .registration { background: #d1ecf1; color: #0c5460; }

  .promo-code-box {
    margin-top: 1rem; background: #e7f3ff; border: 2px dashed #007bff;
    padding: 0.75rem 1.5rem; border-radius: 8px; cursor: pointer;
    display: inline-flex; flex-direction: column; align-items: center;
  }
  .code-val { font-family: monospace; font-size: 1.5rem; font-weight: bold; color: #0056b3; }
  .code-hint { font-size: 0.75rem; color: #666; margin-top: 2px; }

  .main-layout { display: grid; grid-template-columns: 1fr 350px; gap: 2rem; }
  @media (max-width: 900px) { .main-layout { grid-template-columns: 1fr; } }

  .info-card { background: #fff; border: 1px solid #eee; border-radius: 8px; padding: 1.5rem; margin-bottom: 1.5rem; }
  .info-card h3 { margin-top: 0; font-size: 1.25rem; border-bottom: 1px solid #eee; padding-bottom: 0.5rem; margin-bottom: 1rem; }
  .description { font-size: 1.1rem; line-height: 1.6; color: #444; white-space: pre-wrap; }
  
  .btn-link { display: inline-block; margin-top: 1rem; background: #007bff; color: #fff; padding: 0.6rem 1.2rem; border-radius: 6px; text-decoration: none; font-weight: 500; }
  
  .grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; }
  .item.full { grid-column: span 2; }

  .scheme-list { display: flex; flex-direction: column; gap: 0.75rem; margin-bottom: 1.5rem; }
  .scheme-item { background: #f8f9fa; padding: 1rem; border-left: 4px solid #007bff; border-radius: 4px; display: flex; justify-content: space-between; }
  
  .tier-table-container { margin-top: 1.5rem; }
  .tier-table { width: 100%; border-collapse: collapse; margin-top: 0.5rem; }
  .tier-table th, .tier-table td { padding: 0.75rem; border: 1px solid #eee; text-align: left; }
  .tier-table th { background: #f8f9fa; font-size: 0.9rem; }

  .reward-caps { margin-top: 1.5rem; display: flex; gap: 2rem; padding: 1rem; background: #e2f3e5; border-radius: 6px; color: #155724; font-weight: 600; }

  /* Sidebar */
  .sidebar-card { background: #fff; border: 1px solid #eee; border-radius: 8px; padding: 1.25rem; margin-bottom: 1rem; }
  .sidebar-card h3 { font-size: 1rem; margin-top: 0; margin-bottom: 1rem; color: #666; text-transform: uppercase; letter-spacing: 0.5px; }
  
  .btn-action { width: 100%; padding: 0.8rem; border-radius: 6px; border: none; font-weight: bold; cursor: pointer; margin-bottom: 1rem; font-size: 1rem; }
  .btn-action.log { background: #fd7e14; color: #fff; }
  .btn-action.join { background: #28a745; color: #fff; }
  .btn-action.edit-btn { background: #6c757d; color: #fff; text-align: center; display: block; text-decoration: none; box-sizing: border-box; }
  .btn-action.edit-btn:hover { background: #5a6268; }
  .btn-action.delete-btn { background: #dc3545; color: #fff; }
  .btn-action.delete-btn:hover { background: #c82333; }
  .btn-action.rollover-btn { background: #0284c7; color: #fff; }
  .btn-action.rollover-btn:hover { background: #0369a1; }
  .btn-action.rollover-btn:disabled { opacity: 0.6; cursor: not-allowed; }
  .btn-action.ban-btn { background: #6f42c1; color: #fff; }
  .btn-action.ban-btn:hover { background: #5a32a3; }
  .banned-banner { background: #ffebe9; color: #cf222e; border: 1px solid #ff8182; border-radius: 6px; padding: 0.75rem 1rem; margin-bottom: 1rem; font-weight: 500; font-size: 0.95rem; }
  
  .stat-row { font-size: 0.9rem; margin-bottom: 0.5rem; }
  .reg-detail { font-size: 0.85rem; background: #fff8e1; padding: 0.75rem; border-radius: 4px; border: 1px solid #ffe082; }

  .meta-item { display: flex; justify-content: space-between; margin-bottom: 0.5rem; font-size: 0.95rem; }
  .meta-item.vertical { flex-direction: column; gap: 0.5rem; }
  .tags-mini { display: flex; flex-wrap: wrap; gap: 4px; }
  .tags-mini span { background: #eee; padding: 2px 8px; border-radius: 4px; font-size: 0.8rem; }

  .cc-list { display: flex; flex-direction: column; gap: 0.5rem; }
  .cc-item { background: #f8f9fa; padding: 0.5rem; border-radius: 4px; font-size: 0.9rem; display: flex; flex-direction: column; gap: 0.25rem; }
  .btn-who { font-size: 0.75rem; color: #007bff; text-decoration: none; align-self: flex-end; }

  .loc-row { display: flex; justify-content: space-between; margin-bottom: 0.25rem; }
  .loc-details, .loc-sub { font-size: 0.85rem; color: #666; margin: 0.25rem 0 1rem; }

  .detail-footer { margin-top: 3rem; padding-top: 1rem; border-top: 1px solid #eee; color: #999; font-size: 0.85rem; }
  .dot { margin: 0 0.5rem; }
  .status-msg, .error { text-align: center; padding: 3rem; }
  .error { color: #dc3545; font-weight: bold; }
</style>